/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from "react";

// Helper to get the field config by alias
function getFieldConfig(formData: any, alias: string) {
    for (const fieldset of formData.allFieldSets) {
        for (const container of fieldset.containers) {
            for (const field of container.fields) {
                if (field.alias === alias) {
                    return field;
                }
            }
        }
    }
    return undefined;
}

export const useValidation = (formData: any) => {
    const [errorMessages, setErrorMessages] = useState<any[]>([]);

    // --- Field-level validation ---
    function validateField(alias: string, value: any) {
        const field = getFieldConfig(formData, alias);
        if (!field) return;

        let error = undefined;

        // Required
        if (field.mandatory) {
            const hasValue =
                field.fieldTypeId === "08b8057f-06c9-6ca7-3a42-fd1fc2a46eff"
                    ? value && value.size > 0 // File upload
                    : typeof value === "string"
                    ? value.trim().length > 0
                    : Boolean(value);

            if (!hasValue) {
                error = {
                    alias,
                    message:
                        field.requiredErrorMessage ||
                        formData.requiredErrorMessage.replace("{0}", field.alias),
                };
            }
        }

        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if ((alias === "email" || alias === "eMail") && value) {
            const isEmailInvalid = value && !emailRegex.test(value);
            const shouldValidateEmail = field.mandatory || (value && value.trim().length > 0);

            if (shouldValidateEmail && isEmailInvalid) {
                error = {
                    alias,
                    message:
                        field.requiredErrorMessage ||
                        formData.requiredErrorMessage.replace("{0}", field.alias),
                };
            }
        }

        setErrorMessages((prev: any[]) => {
            const filtered = prev ? prev.filter((e) => e.alias !== alias) : [];
            return error ? [...filtered, error] : filtered;
        });
    }

    function validate(body: any) {
        const tmpErrorMessages: any[] = [];
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        formData.allFields.forEach((field: any) => {
            let hasValue;
            if (
                body?.[field.alias] &&
                field.fieldTypeId === "08b8057f-06c9-6ca7-3a42-fd1fc2a46eff"
            ) {
                hasValue = body?.[field.alias]?.size > 0;
            } else {
                hasValue =
                    typeof body?.[field.alias] === "string"
                        ? body[field.alias].trim().length > 0
                        : Boolean(body?.[field.alias]);
            }

            if (hasValue === false && field.mandatory) {
                tmpErrorMessages.push({
                    alias: field.alias,
                    message:
                        field.requiredErrorMessage ||
                        formData.requiredErrorMessage.replace("{0}", field.alias),
                });
            }

            // Email validation
            if (field.alias === "eMail" || field.alias === "email") {
                const emailValue = body[field.alias];
                const isEmailInvalid = emailValue && !emailRegex.test(emailValue);
                const shouldValidateEmail =
                    field.mandatory || (emailValue && emailValue.trim().length > 0);

                if (shouldValidateEmail && isEmailInvalid) {
                    tmpErrorMessages.push({
                        alias: field.alias,
                        message:
                            field.requiredErrorMessage ||
                            formData.requiredErrorMessage.replace("{0}", field.alias),
                    });
                }
            }
        });

        setErrorMessages(tmpErrorMessages);

        return [tmpErrorMessages.length === 0, tmpErrorMessages];
    }

    function addErrors(errors?: any) {
        const tmpErrorMessages: any[] = [];
        if (!errors || typeof errors !== "object") return;

        for (const alias in errors) {
            if (Object.prototype.hasOwnProperty.call(errors, alias)) {
                const messages = errors[alias];
                if (Array.isArray(messages)) {
                    messages.forEach((message) => {
                        if (typeof message === "string") {
                            tmpErrorMessages.push({ alias, message });
                        }
                    });
                } else if (typeof messages === "string") {
                    tmpErrorMessages.push({ alias, message: messages });
                }
            }
        }
        setErrorMessages(tmpErrorMessages);
    }

    function resetError(alias: string) {
        setErrorMessages((prev: any[]) => prev.filter((x) => x.alias !== alias));
    }

    function getError(alias: string) {
        return errorMessages?.find((x) => x.alias === alias);
    }

    return {
        errorMessages,
        validate,
        getError,
        addErrors,
        onInputChange: validateField, // you can alias this for clarity
        resetError,
        validateField,
    };
};
